import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { UserPrivilege } from '../_model/user_privilege';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationBetaNewService {
  readonly ApiUrl: string = environment.appUrl;
  public currentUserSubject: BehaviorSubject<any> = new BehaviorSubject({});
  public loginUser = new BehaviorSubject<boolean>(false);
  private token_checked = new BehaviorSubject<boolean>(false);
  public userPrivilegeSubject = new BehaviorSubject<UserPrivilege | null>(null);
  userPrivilege$ = this.userPrivilegeSubject.asObservable();

  constructor(private http: HttpClient) {
    this.getSavedUser();

    const storedPriv = localStorage.getItem('user_privilege') || localStorage.getItem('userPrivilege');
    this.userPrivilegeSubject.next(
      storedPriv ? JSON.parse(storedPriv) : null
    );

    // Add the storage event listener here in the constructor
    window.addEventListener('storage', (event) => {
      if (event.key === 'auth-event') {
        if (event.newValue === 'login') {
          const savedUser = localStorage.getItem('currentUser');
          if (savedUser) {
            this.sendCurrentUserSubject(JSON.parse(savedUser));
            this.sendLoginUser(true);
          }
        } else if (event.newValue === 'logout') {
          this.sendCurrentUserSubject({});
          this.sendLoginUser(false);
        }
      }
    });
  }

  getSavedUser() {
    if (localStorage.getItem('currentUser')) {
      let saved_user: any = localStorage.getItem('currentUser');
      this.sendCurrentUserSubject(JSON.parse(saved_user));
      this.sendLoginUser(true);
    }
  }

  // postdata = { username, password }
  // login(postdata: {}): Observable<any> {
  //   return this.http.post<any>(`${ this.ApiUrl } / signin`, postdata)
  //     .pipe(map(user => {
  //       // store user details and jwt token in local storage to keep user logged in between page refreshes
  //       localStorage.setItem('currentUser', JSON.stringify(user));
  //       this.currentUserSubject.next(user);
  //       this.startRefreshTokenTimer();
  //       return user;
  //     }));
  // }
  login(postdata: {}): Observable<any> {
    return this.http.post<any>(`${this.ApiUrl}/signin`, postdata).pipe(
      map((user: any) => {
        // Check if user is active/inactive or error response
        if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
          const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
          throw new Error(errMsg);
        }

        // Store user details and token in localStorage
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.extractAndSavePrivilege(user);

        // Broadcast login event
        localStorage.setItem('auth-event', 'login'); // Notify other tabs

        this.currentUserSubject.next(user);
        this.sendLoginUser(true);
        this.startRefreshTokenTimer(); // You need to implement this
        return user;
      })
    );
  }

  user_login(postdata: {}): Observable<any> {
    return this.http
      .post<any>(`${this.ApiUrl}/api/v2/user/auth-login`, postdata)
      .pipe(
        map((user) => {
          // Check if user is active/inactive or error response
          if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
            const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
            throw new Error(errMsg);
          }

          // ✅ Save full user data
          localStorage.setItem('currentUser', JSON.stringify(user));

          // ✅ Extract and save privilege data
          this.extractAndSavePrivilege(user);

          localStorage.setItem('auth-event', 'login');
          this.currentUserSubject.next(user);
          this.sendLoginUser(true);
          this.startRefreshTokenTimer();

          return user;
        })
      );
  }

  auto_login(accesskey: {}): Observable<any> {
    return this.http.post<any>(`${this.ApiUrl}/api/v2/user/auto-login`, accesskey).pipe(
      map((user) => {
        // Check if user is active/inactive or error response
        if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
          const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
          throw new Error(errMsg);
        }
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.extractAndSavePrivilege(user);
        this.currentUserSubject.next(user);
        this.sendLoginUser(true);
        this.startRefreshTokenTimer();
        return user;
      })
    );
  }

  auto_login2(accesskey: {}): Observable<any> {
    return this.http.post<any>(`${this.ApiUrl}/api/v2/user/auto-login`, { accesskey }).pipe(
      map((user) => {
        // Check if user is active/inactive or error response
        if (!user || user.status === 'error' || user.status === 'inactive' || user.status === 'suspended' || user.status === 'failed' || user.status === 0 || user.is_active === false || user.is_active === 0 || user.is_active === '0' || user.isActive === false || user.error) {
          const errMsg = user?.message || user?.error || 'Access denied: Account is suspended/inactive';
          throw new Error(errMsg);
        }
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem('currentUser', JSON.stringify(user));
        this.extractAndSavePrivilege(user);
        this.currentUserSubject.next(user);
        this.sendLoginUser(true);
        this.startRefreshTokenTimer();
        return user;
      })
    );
  }

  public extractAndSavePrivilege(user: any): any {
    if (!user) return null;
    const userId = user.userid || user.userId || user.id;
    let userPrivilege: any = null;

    if (user.user_privilege) {
      if (userId && user.user_privilege[`user_${userId}`]) {
        userPrivilege = user.user_privilege[`user_${userId}`]['ipd-mongo-db']
          || user.user_privilege[`user_${userId}`]['ipd']
          || user.user_privilege[`user_${userId}`]['IPD']
          || user.user_privilege[`user_${userId}`];
      }
      if (!userPrivilege) {
        userPrivilege = user.user_privilege['ipd-mongo-db']
          || user.user_privilege['ipd']
          || user.user_privilege['IPD']
          || (typeof user.user_privilege === 'object' && !user.user_privilege[`user_${userId}`] ? user.user_privilege : null);
      }
    }

    if (userPrivilege) {
      userPrivilege.View = userPrivilege.View === true || userPrivilege.View === 'true' || userPrivilege.View === 1 || userPrivilege.View === '1' || userPrivilege.View === 'yes';
      userPrivilege.Download = userPrivilege.Download === true || userPrivilege.Download === 'true' || userPrivilege.Download === 1 || userPrivilege.Download === '1' || userPrivilege.Download === 'yes';

      userPrivilege.Search = Number(userPrivilege.Search ?? 0);
      userPrivilege.DownloadCount = Number(userPrivilege.DownloadCount ?? userPrivilege.InitialDownloadCount ?? 0);
      userPrivilege.DailySearchLimit = Number(userPrivilege.DailySearchLimit ?? 0);
      userPrivilege.DailyDownloadLimit = Number(userPrivilege.DailyDownloadLimit ?? 0);

      const existingPrivStr = localStorage.getItem('user_privilege') || localStorage.getItem('userPrivilege');
      if (existingPrivStr) {
        try {
          const parsed = JSON.parse(existingPrivStr);
          userPrivilege.today_search_count = Number(parsed.today_search_count ?? 0);
          userPrivilege.today_download_count = Number(parsed.today_download_count ?? 0);
        } catch (e) {
          userPrivilege.today_search_count = 0;
          userPrivilege.today_download_count = 0;
        }
      } else {
        userPrivilege.today_search_count = 0;
        userPrivilege.today_download_count = 0;
      }

      localStorage.setItem('user_privilege', JSON.stringify(userPrivilege));
      localStorage.setItem('userPrivilege', JSON.stringify(userPrivilege));

      this.userPrivilegeSubject.next(userPrivilege);
      return userPrivilege;
    }
    return null;
  }

  // logout() {
  //   // remove user from local storage and set current user to null
  //   localStorage.removeItem('currentUser');
  //   this.sendCurrentUserSubject({});
  //   this.sendLoginUser(false);
  //   // this.currentUserSubject.next(null);
  //   // this._constant.isLoggedIn = false;
  //   // let currentaccesskey = this.route.snapshot.queryParams['access_key'];
  //   // let newurlroute = this.router.url.replace(?access_key = ${ currentaccesskey }, "");

  //   //this.router.navigateByUrl(newurlroute);
  //   //window.location.reload();
  // }
  logout() {
    // Remove the user from localStorage
    localStorage.removeItem('currentUser');

    // Broadcast logout event
    localStorage.setItem('auth-event', 'logout'); // Notify other tabs

    this.currentUserSubject.next({});
    this.sendLoginUser(false);
  }

  refreshToken() {
    let currentuser = localStorage.getItem('currentUser');
    let var_userid = 0;
    let email = '';
    if (currentuser) {
      let userdata = JSON.parse(currentuser);
      var_userid = userdata.userid;
      email = userdata.email;
    }
    return this.http
      .post<any>(`${this.ApiUrl}/api/v2/user/refresh-token`, {
        userid: var_userid,
        email: email,
      })
      .pipe(
        map((user) => {
          localStorage.setItem('currentUser', JSON.stringify(user));
          this.sendCurrentUserSubject(user);
          this.sendLoginUser(true);
          this.startRefreshTokenTimer();
          return user;
        })
      );
  }

  // getUserPrivilege(postdata: {}) {
  //   return this.http.post(this.ApiUrl + '/usermaster_manage', postdata);
  // }

  // helper methods

  private refreshTokenTimeout: any;

  private startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token
    // const jwtToken = JSON.parse(atob(this.currentUserValue.token.split('.')[1]));
    // set a timeout to refresh the token a minute before it expires
    // const expires = new Date(jwtToken.exp * 1000);
    // const timeout = expires.getTime() - Date.now() - (60 * 1000);
    // this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe(), timeout);
  }

  private stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  sendLoginUser(data: any) {
    this.loginUser.next(data);
  }
  isAuthenticated(): boolean {
    return this.loginUser.getValue(); // Returns true if the user is logged in
  }
  getLoginUser() {
    return this.loginUser.asObservable();
  }

  sendCurrentUserSubject(data: any) {
    this.currentUserSubject.next(data);
  }

  getCurrentUserSubject() {
    return this.currentUserSubject.asObservable();
  }

  sendTokenChecked(data: any) {
    this.token_checked.next(data);
  }

  getTokenChecked() {
    return this.token_checked.asObservable();
  }
}
